import React, { useState, useEffect, useReducer } from 'react';

import './Stamp.single.scss';
import { BaseReact } from '../../base.model';
import { ProfileQL } from '../../models/Profile';
import { useQuery, useMutation, useLazyQuery } from '@apollo/client';
import { graphqlSchema } from '../../services/graphql.schema';
import { AmazonOrderBinder } from '../../models/Orders';
import { SearchOrderBinded, SearchOrderWithProducts, SearchOrderItemBinded } from '../../models/Orders_v2';
import { orderToConceptos } from '../../utils/orders/order.toConceptos';
import SectionTitleBar from '../components/titlebar';
import Select from '../Forms/Select';
import Input from '../Forms/Input';
import Loading from '../Animations/loadScreen';
import { numberToCurrencyString } from '../../services/formatting';
import { Bill, BillBase, BillReceptor, BillConceptos, BillRelacionados } from '../../models/Factura';

import { calculateTotalFromConcepts } from '../../services/calculations.cfdi';
import Button from '../Forms/Button';
import StampSingleBilled from './Stamp.single.billed';
import { FiscalPopError, FiscalPopErrorBadReq } from '../../models/Fiscalpop';
import CopyToClipboard from '../visuals/clipboard.copy';
import { Link } from 'react-router-dom';
import sharedToasterSubject from '../../services/shared.toasterSubject';
import StampRefund from './Stamp.refund';
import StampRefundBilled from './Stamp.refund.billed';
import { REGIMEN_OPTIONS } from '../../utils/regimen';

interface SetupProps extends BaseReact {
    currentUser: ProfileQL;
}

const itemBinderMapReducer = (state: { [key: string]: SearchOrderItemBinded }, items: SearchOrderItemBinded[]) => {
    for (const item of items) {
        if (!state[item.item.product.asin]) {
            state[item.item.product.asin] = item;
        }
    }
    return Object.assign({}, state);
}

function cfdiReducer(state: any, action: { type: 'receptor' | 'conceptos' | 'comprobante' | 'relacionados'; payload: BillBase | BillReceptor | BillConceptos[] | BillRelacionados }): Bill {
    switch (action.type) {
        case 'receptor':
            const receptor = Object.assign({}, state.receptor, action.payload as BillReceptor);
            state.receptor = receptor;
            return Object.assign({}, state);
        case 'conceptos':
            state.conceptos = action.payload as BillConceptos[];
            console.log('CFDI Reducer, conceptos: ', state);
            return Object.assign({}, state);
        case 'comprobante':
            console.log('cfdiReducer (COMPROBANTE): ', action.payload);
            return Object.assign({}, state, action.payload as BillBase)
        case 'relacionados':
            const cfdiRelacionados = Object.assign({}, state.cfdiRelacionados || {}, action.payload as BillRelacionados);
            state.cfdiRelacionados = cfdiRelacionados;
            return Object.assign({}, state);
        default:
            return state;
    }
}

function StampSingle({ history, location, currentUser }: SetupProps) {
    // CFDI ========
    const [cfdi, assignSetCfdi] = useReducer(cfdiReducer, {
        fecha: new Date().toJSON(),
        serie: 'SNG',
        folio: '01',
        formaPago: '',
        metodoPago: 'PUE',
        lugarExpedicion: currentUser.fiscalpopProfile.lugarExpedicion || '',
        tipoDeComprobante: 'I',
        receptor: {
            nombre: 'PUBLICO EN GENERAL',
            rfc: 'XAXX010101000',
            usoCFDI: 'G03',
            email: '',
            regimen: '612',
            zip: ''
        },
        conceptos: [],
    } as Bill);

    const setComprobanteProp = (property: 'fecha' | 'serie' | 'folio' | 'formaPago' | 'metodoPago' | 'lugarExpedicion') => {
        return (value: string | 'PUE' | 'PPD') => {
            console.log(`setComprobanteProp - ${property}: `, cfdi);
            const { fecha, serie, folio, formaPago, metodoPago, lugarExpedicion, tipoDeComprobante } = cfdi;
            const newCfdi: BillBase = { fecha, serie, folio, formaPago, metodoPago, lugarExpedicion, tipoDeComprobante };
            if (property === 'metodoPago') {
                newCfdi[property] = value as 'PUE';
            } else {
                newCfdi[property] = value;
            }
            assignSetCfdi({ type: 'comprobante', payload: newCfdi });
        }
    }
    const setReceptorProp = (property: 'nombre' | 'rfc' | 'usoCFDI' | 'email' | 'regimen' | 'zip') => {
        return (value: string) => {
            const newReceptor = cfdi.receptor;
            if (property === 'rfc') {
                newReceptor[property] = value.toUpperCase();
            } else if (property === 'email') {
                newReceptor[property] = value.toLowerCase();
            } else {
                newReceptor[property] = value;
            }
            assignSetCfdi({ type: 'receptor', payload: newReceptor });
        }
    }
    const [orderToBill, setOderToBill] = useState<SearchOrderWithProducts>(null);
    const [dateOffset, setDateOffset] = useState<number>(0);
    const [fpopError, setFpopError] = useState<FiscalPopError>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    // =============
    const [billLink, setBillLink] = useState('');
    const [manualOverride, setManualOverride] = useState(false);
    // =============
    const [itemBindersMap, assignItemBinder] = useReducer(itemBinderMapReducer, {} as { [key: string]: SearchOrderItemBinded });
    const [usosCFDIoptions, setUsosCFDIoptions] = useState<{ clave: string, descripcion: string }[]>([]);
    const [formaPagoOptions, setFormaPagoOptions] = useState<{ key: string, label: string }[]>([]);

    useQuery(graphqlSchema.PROFILE.ORDERS.getOrderById_v2, {
        variables: {
            id: location.pathname.replace('/ordenes/stamp/', '')
        },
        onCompleted: ({ getOrderById_v2 }: { getOrderById_v2: SearchOrderWithProducts }) => {
            console.log('Order gotten: ', getOrderById_v2);
            setOderToBill(JSON.parse(JSON.stringify(getOrderById_v2)));
            assignItemBinder(JSON.parse(JSON.stringify(getOrderById_v2.products)));
            setComprobanteProp('formaPago')('31');
            setBillLink(`https://apimws.fiscalpop.com/facturacion/${currentUser.amazonProfile.mwsSelledId}/${getOrderById_v2.order.order.orderId}?o=manual`);
            // console.log('Order gotten ids for items: ', getOrderById.order.order_items.map(oi => oi.item.id));
        },
        onError: (e) => {
            sharedToasterSubject.next({ type: 'error', message: e.graphQLErrors[0].message });
        },
        fetchPolicy: 'cache-and-network'
    });

    const [refreshOrderById] = useLazyQuery(graphqlSchema.PROFILE.ORDERS.getOrderById_v2, {
        variables: {
            id: location.pathname.replace('/ordenes/stamp/', '')
        },
        onCompleted: ({ getOrderById_v2 }: { getOrderById_v2: SearchOrderWithProducts }) => {
            console.log('Order gotten: ', getOrderById_v2);
            setOderToBill(JSON.parse(JSON.stringify(getOrderById_v2)));
            assignItemBinder(JSON.parse(JSON.stringify(getOrderById_v2.products)));
            setComprobanteProp('formaPago')('31');
            setBillLink(`https://apimws.fiscalpop.com/facturacion/${currentUser.amazonProfile.mwsSelledId}/${getOrderById_v2.order.order.orderId}?o=manual`);
            // console.log('Order gotten ids for items: ', getOrderById.order.order_items.map(oi => oi.item.id));
        },
        onError: (e) => {
            sharedToasterSubject.next({ type: 'error', message: e.graphQLErrors[0].message });
        },
        fetchPolicy: 'network-only'
    });

    const [stampCfdi] = useMutation(graphqlSchema.FISCALPOP.CFDI.setBillToStamp, {
        onCompleted: ({ setBillToStamp }: { setBillToStamp: AmazonOrderBinder }) => {
            console.log('Stamped CFDI: ', setBillToStamp);
            const _orderToBill: SearchOrderWithProducts = JSON.parse(JSON.stringify(orderToBill))
            _orderToBill.order.binder = setBillToStamp;
            // Make sure to remove refundPayments if this stamping includes refunds
            setOderToBill(Object.assign({}, _orderToBill));
            setIsSubmitting(false);
            sharedToasterSubject.next({ type: 'confirm', message: 'Se emitió la factura correctamente' });
        },
        onError: (e) => {
            setIsSubmitting(false);
            console.log(`e.graphQLErrors[0]: `, e.graphQLErrors[0]);
            sharedToasterSubject.next({ type: 'error', message: 'Error al generar factura, revise datos de receptor y claves de producto', clickToClose: true });
        }
    })

    const fetchUsoCfdi = (authToken: string) => {
        fetch(`https://api.fiscalpop.com/api/v1/sat/usoCfdi/${authToken}`)
            .then(d => d.json())
            .then(data => setUsosCFDIoptions(data))
            .catch(e => {

            });
    }

    const fetchFormasPago = (authToken: string) => {
        fetch(`https://api.fiscalpop.com/api/v1/sat/payTypes/${authToken}`)
            .then(d => d.json())
            .then(data => setFormaPagoOptions(data))
            .catch(e => {

            });
    }

    const reFetchOrder = () => {
        refreshOrderById();
    }

    const setDateOverride = (dateOffset: 0 | 1 | 2 | 3) => {
        setDateOffset(dateOffset);
        if (dateOffset === 0) {
            const fecha = new Date().toJSON();
            setComprobanteProp('fecha')(fecha);
        } else {
            const fecha = new Date(Date.now() - (dateOffset * 24 * 60 * 60 * 1000) + (20 * 1000) - (1000 * 60 * 60 * 1)).toJSON();
            setComprobanteProp('fecha')(fecha);
        }
    }

    useEffect(() => {
        setComprobanteProp('folio')(`${location.pathname.replace('/ordenes/stamp/', '')}`);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location])

    useEffect(() => {
        fetchFormasPago(currentUser.amazonProfile.authToken);
        fetchUsoCfdi(currentUser.amazonProfile.authToken);
    }, [currentUser]);

    useEffect(() => {
        const isCancelled = !!orderToBill && !!orderToBill.order ? orderToBill.order.order.orderStatus === 'CANCELLED' : false;
        const isPending = !!orderToBill && !!orderToBill.order ? orderToBill.order.order.orderStatus === 'PENDING' : false;
        if (!(isCancelled || isPending)) {
            if (!!Object.keys(itemBindersMap).length && orderToBill && orderToBill.products.every(c => !!itemBindersMap[c.item.product.asin])) {
                const billConcepts = orderToConceptos(orderToBill.products, itemBindersMap);
                console.log('Assigning conceptos: ', billConcepts);
                assignSetCfdi({ type: 'conceptos', payload: billConcepts });
            }
        }
    }, [itemBindersMap, orderToBill]);

    const hasError = (property: string, elementIndex?: number, elementProperty?: string) => {
        if (!fpopError) {
            return false
        }
        const brqCandidate = fpopError.badRequests.find(brq => !!brq[property]);
        if (!brqCandidate) {
            return false;
        } else if (!elementIndex && elementIndex !== 0) {
            return true;
        }
        const _elementCandidate: FiscalPopErrorBadReq[] = brqCandidate[property] as FiscalPopErrorBadReq[];
        const elementCandidate = _elementCandidate[elementIndex];
        if (!elementCandidate) {
            return false;
        } else if (!elementProperty) {
            return true;
        }
        return !!elementCandidate.property && (elementCandidate.property === elementProperty) ? true : false;
    }

    const dateOffsetOptions = [
        { value: 0, label: 'Hoy mismo' },
        { value: 1, label: 'Hace 1 día' },
        { value: 2, label: 'Hace 2 días' },
        { value: 3, label: 'Hace 3 días' },
    ];
    const formasOptions = formaPagoOptions.map(f => ({ label: `(${f.key}) ${f.label}`, value: f.key }));
    const usoOptions = usosCFDIoptions.map((e) => ({ value: e.clave, label: `(${e.clave}) ${e.descripcion}` }));

    const submitCFDI = () => {
        console.log('CFDI: ', cfdi);
        if (!cfdi.receptor.zip && (cfdi.receptor.rfc !== 'XAXX010101000')) {
            setFpopError({
                code: 'RECEPTOR-ZIP', message: 'Falta Código postal receptor', badRequests: [{
                    'receptor': [{
                        property: 'zip',
                        code: 'RECEPTOR-ZIP',
                        message: 'Falta agregar Código postal del receptor',
                        explanation: '',
                        moreInfo: ''
                    }]
                }]
            })
            return;
        }

        setFpopError(null);
        if (cfdi.receptor.rfc === 'XAXX010101000') {
            cfdi.receptor.zip = currentUser.fiscalpopProfile.lugarExpedicion;
        }
        setIsSubmitting(true);
        stampCfdi({
            variables: {
                cfdi,
                orderId: orderToBill.order.order.orderId,
                customerId: null,
                refundIds: []
            }
        })
    }

    const handleManualOverride = () => {
        setManualOverride(true);
    }

    const updateBinderFromRefund = (orderBinder: SearchOrderBinded) => {
        const orderToBillUpdated = Object.assign({}, orderToBill);
        orderToBillUpdated.order = orderBinder;
        setOderToBill(orderToBillUpdated);
    }

    // RENDER METHODS =============
    // ============================
    const renderTotals = () => {
        if (!Object.keys(itemBindersMap).length || !cfdi.conceptos.length) {
            return '';
        }

        const { subtotal, iva, iepsCuota, iepsTasa, total } = calculateTotalFromConcepts(cfdi.conceptos);

        return (
            <div className="cfdiTotal">
                <p className="_subtotal">
                    <b>Subtotal:</b> $ {numberToCurrencyString(subtotal)}
                </p>
                <div className="_taxes">
                    <p><b>IVA:</b> $ {numberToCurrencyString(iva)}</p>
                    {
                        iepsCuota ?
                            <p><b>IEPS cuota:</b> $ {numberToCurrencyString(iepsCuota)}</p>
                            : ''
                    }
                    {
                        iepsTasa ?
                            <p><b>IEPS tasa:</b> $ {numberToCurrencyString(iepsTasa)}</p>
                            : ''
                    }
                </div>
                <p className="_total">
                    <b>Total: </b> $ {numberToCurrencyString(total)}
                </p>
            </div>
        )
    }

    const renderItemList = () => {
        if (!Object.keys(itemBindersMap).length || !cfdi.conceptos.length) {
            return '';
        }
        return (
            <div className="itemList">
                <div className="lineItem header">
                    <p>
                        Producto
                    </p>
                    <p className='_unitary'>
                        Precio unitario
                    </p>
                    <p className='_keys'>
                        Clave SAT
                    </p>
                    <p className='_tax'>
                        Impuestos
                    </p>
                </div>
                {
                    cfdi.conceptos.map((concepto, i) => {
                        // Taxes =====
                        const taxes = concepto.impuestos;
                        const iva = taxes.find(t => t.type === 'iva');
                        const ieps = taxes.filter(t => t.type === 'ieps');
                        const iepsCuota = ieps.reduce((p, c) => p += (c.cuota || 0), 0);
                        const iepsTasa = ieps.reduce((p, c) => p += (c.tasa || 0), 0);
                        console.log('concepto: ', concepto);
                        console.log('iva: ', iva);
                        // ===========
                        return (
                            <div className="lineItem" key={i}>
                                {
                                    concepto.claveProdServ === '78102203' ?
                                        <div className="itemShipping">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24">
                                                <path d="M20 8h-3V4H3c-1.1 0-2 .9-2 2v11h2c0 1.66 1.34 3 3 3s3-1.34 3-3h6c0 1.66 1.34 3 3 3s3-1.34 3-3h2v-5l-3-4zM6 18.5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm13.5-9l1.96 2.5H17V9.5h2.5zm-1.5 9c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5z" />
                                            </svg>
                                            <div className="_product">
                                                <p>
                                                    {concepto.descripcion}
                                                </p>
                                                {concepto.noIdentificacion ? <span className="noIdentificacion">{concepto.noIdentificacion}</span> : ''}
                                            </div>
                                        </div>
                                        :
                                        <div className="_product">
                                            <p>
                                                {concepto.descripcion}
                                            </p>
                                            {concepto.noIdentificacion ? <span className="noIdentificacion">{concepto.noIdentificacion}</span> : ''}
                                            {concepto.pedimento && concepto.pedimento?.length ? <span className="noIdentificacion">Pedimento: {concepto.pedimento[0]}</span> : ''}
                                        </div>
                                }
                                <p>
                                    $ {numberToCurrencyString(concepto.valorUnitario)} <b>(x {concepto.cantidad})</b>
                                </p>
                                <div className="_keys">
                                    <p>{concepto.claveProdServ}</p>
                                    <p>{concepto.claveUnidad}</p>
                                    <p className="_isCustomized">{concepto.claveProdServ !== currentUser.amazonProfile.defaultClaveSatProdServ ? '' : '(Default)'}</p>
                                </div>
                                <div className="_taxes">
                                    {
                                        iepsTasa ?
                                            <p className="_ieps">
                                                <b>IEPS ({(iepsTasa * 100).toFixed(1)} %): </b> $ {numberToCurrencyString(iepsTasa * concepto.valorUnitario)}
                                            </p>
                                            : ''
                                    }
                                    {
                                        iepsCuota ?
                                            <p className="_ieps">
                                                <b>IEPS (Cuota): </b> $ {(iepsCuota).toFixed(2)}
                                            </p>
                                            : ''
                                    }
                                    <p className="_iva">
                                        <b>IVA ({iva.exento ? 'Exento' : (iva.tasa * 100).toFixed(0) + '%'}):</b> $ {numberToCurrencyString(iva.tasa * ((1 + iepsTasa) * concepto.valorUnitario))}
                                    </p>
                                </div>
                            </div>
                        )
                    })
                }
            </div>
        )
    }


    const applyManualOverride = !!orderToBill && !!orderToBill.order.binder ? !(orderToBill.order.binder.billIsGlobal && manualOverride) : true;
    const isCancelled = !!orderToBill && !!orderToBill.order ? orderToBill.order.order.orderStatus === 'CANCELLED' : false;
    const isPending = !!orderToBill && !!orderToBill.order ? orderToBill.order.order.orderStatus === 'PENDING' : false;
    if (!orderToBill) {
        return (
            <div id="StampSingle">
                <SectionTitleBar currentUser={currentUser} title={`Facturar pedido: ${location.pathname.replace('/ordenes/stamp/', '')}`} />
                <Loading display={true} />
            </div>
        )
    } else if (!!orderToBill && !!orderToBill.order.binder && applyManualOverride) {
        return (
            <div id="StampSingle">
                <SectionTitleBar currentUser={currentUser} title={`${orderToBill.order.order.orderStatus !== 'SHIPPED' ? 'Reembolso de pedido' : 'Pedido Facturado'}: ${location.pathname.replace('/ordenes/stamp/', '')}`} />
                {
                    (orderToBill.order.order.orderStatus === 'CANCELLED' && !orderToBill.order.binder.refundBillUUID?.length) ?
                        <StampRefund orderBilled={orderToBill.order} currentUser={currentUser} handleManualOverride={handleManualOverride} updateBinderFromRefund={updateBinderFromRefund} />
                        : (orderToBill.order.binder.refundBillUUID || []).map((refund, i) => {
                            return (<StampRefundBilled originalUUID={orderToBill.order.binder.billUUID} refundBilled={refund} currentUser={currentUser} key={refund.uuid} />)
                        })
                }
                <StampSingleBilled orderBilled={orderToBill.order} currentUser={currentUser} handleManualOverride={handleManualOverride} reFetchOrder={reFetchOrder} />
            </div>
        )
    } else if (isCancelled) {
        return (
            <div id="StampSingle">
                <SectionTitleBar currentUser={currentUser} title={`${orderToBill.order.order.orderStatus !== 'SHIPPED' ? 'Reembolso de pedido' : 'Pedido Facturado'}: ${location.pathname.replace('/ordenes/stamp/', '')}`} />
                <div className="stampContent">
                    <h1>
                        Pedido cancelado
                    </h1>
                    <div className="card">
                        <p>
                            Pedidos cancelados no pueden facturarse
                        </p>
                    </div>
                </div>
            </div>
        )
    } else if (isPending) {
        return (
            <div id="StampSingle">
                <SectionTitleBar currentUser={currentUser} title={`${orderToBill.order.order.orderStatus === 'CANCELLED' ? 'Reembolso de pedido' : 'Pedido Pendiente'}: ${location.pathname.replace('/ordenes/stamp/', '')}`} />
                <div className="stampContent">
                    <div className='_isEmpty'>
                        <h2>
                            Pedido pendiente de entregarse
                        </h2>
                        <p>
                            Pedidos pendientes de entregarse no pueden facturarse todavía.
                        </p>
                    </div>
                </div>
            </div>
        )
    } else {
        console.log('OrderToBill: ', orderToBill);
        return (
            <div id="StampSingle">
                <SectionTitleBar currentUser={currentUser} title={`Facturar pedido: ${location.pathname.replace('/ordenes/stamp/', '')}`} />
                <div className="stampContent">
                    <CopyToClipboard textLabel="Copiar link de facturación en línea" value={`${billLink}${manualOverride ? '&override=' + orderToBill.order.binder.billUUID : ''}`} />


                    <div className="card billBody">
                        <div className="row four md-two">
                            <Select value={dateOffset} onChange={setDateOverride} label={'Fecha de factura'} options={dateOffsetOptions} />
                        </div>
                        <div className="row four lg-two sm-one">
                            <Input type="text" label="Cliente a facturar" placeholder="Cliente a facturar" value={`${orderToBill.order.order.buyer?.buyerName || ''}`} disabled={true} onChange={() => ''} />
                            <Select value={cfdi.formaPago} onChange={setComprobanteProp('formaPago')} hasError={hasError('formaPago')} label={'Forma de Pago'} options={formasOptions} />
                            <Input type="text" label="Método de pago" placeholder="Método de pago" value={`(PUE) Un solo Pago`} disabled={true} onChange={() => ''} />
                            <Input type="text" label="Lugar de expedición" placeholder="Lugar de expedición" value={cfdi.lugarExpedicion} hasError={hasError('lugarExpedicion')} onChange={setComprobanteProp('lugarExpedicion')} />
                        </div>
                        <div className="divider"></div>
                        <div className="row two sm-one">
                            <Input type="text" label="Razón social" placeholder="Razón social del receptor" value={cfdi.receptor.nombre} hasError={hasError('receptor', 0, 'name')} onChange={setReceptorProp('nombre')} />
                            <Input type="text" label="RFC" placeholder="RFC del receptor" value={cfdi.receptor.rfc} hasError={hasError('receptor', 0, 'rfc')} onChange={setReceptorProp('rfc')} />


                            <Select value={cfdi.receptor.regimen} onChange={setReceptorProp('regimen')} label={'Régimen'} hasError={hasError('receptor', 0, 'regimen')} options={REGIMEN_OPTIONS} />
                            <Input type="text" label="Código postal receptor" placeholder="Codigo postal" value={cfdi.receptor.zip} hasError={hasError('receptor', 0, 'zip')} onChange={setReceptorProp('zip')} />


                            <Select value={cfdi.receptor.usoCFDI} onChange={setReceptorProp('usoCFDI')} label={'Uso CFDI'} hasError={hasError('receptor', 0, 'usoCFDI')} options={usoOptions} />
                            <Input type="text" label="Email" placeholder="Email del receptor" value={cfdi.receptor.email} onChange={setReceptorProp('email')} />
                        </div>
                        <div className="divider whole"></div>
                        {renderItemList()}
                        {renderTotals()}
                    </div>
                    <div className="actions">
                        <div className={`_FpopErrors ${fpopError ? 'hasError' : ''}`}>
                            {
                                fpopError ?
                                    <p>
                                        <b>(code: {fpopError.code}) </b> {fpopError.message}
                                    </p> :
                                    (!currentUser.fiscalpopProfileStatus.modulusMatch ?
                                        <p className="defaultError">
                                            <b>Falta subir CSD </b> Es necesario subir su CSD en <Link to="/setup?showcerts=true">"Mi configuración"</Link> antes de poder factuar
                                        </p>
                                        : '')
                            }
                            {
                                fpopError ?
                                    <ul>
                                        {
                                            fpopError.badRequests.map((breq, i) => {
                                                const breqProp = Object.keys(breq)[0];
                                                const breqEntry = Array.isArray(breq[breqProp]) ? (breq[breqProp] as FiscalPopErrorBadReq[])[0] : breq[breqProp] as FiscalPopErrorBadReq;
                                                return (
                                                    <li key={i}>
                                                        <p>
                                                            <b>{breqProp}:</b> {breqEntry.message}
                                                        </p>
                                                    </li>
                                                )
                                            })
                                        }
                                    </ul>
                                    : ''
                            }
                        </div>
                        <Button primary={true} handleClick={submitCFDI} disabled={isSubmitting || !currentUser.fiscalpopProfileStatus.modulusMatch}>
                            <span>{isSubmitting ? 'Facturando...' : 'Facturar'}</span>
                        </Button>
                    </div>
                </div>
            </div>
        )
    }
}

export default StampSingle;